/**
 * Vista del participante NOMINAL (modo "uneven" o lista de espera).
 *
 * Sin formulario: token único en la URL → el miembro ve SU importe.
 * - Si `organizer_already_paid`: header TERRACOTA "Devolución para",
 *   importe enorme (Spectral 52px) y CTA "Devolver mi parte — X €".
 * - Si lista de espera: tarjeta editorial paciente, sin acción de pago.
 * - Si ya pagó: banda olive "Ya pagaste. Gracias.".
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';


interface ParticipantView {
  participant: {
    name: string;
    custom_amount: number;
    category?: string;
    is_waitlist: boolean;
    status: string;
  };
  group_buy: {
    slug: string;
    title: string;
    organizer_name: string;
    type: string;
    organizer_already_paid: boolean;
    beneficiary_label?: string;
  };
}


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function GroupBuyParticipantPage() {
  const { slug, accessToken } = useParams<{ slug: string; accessToken: string }>();
  const [data, setData] = useState<ParticipantView | null>(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState<'stripe' | 'bizum'>('stripe');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!slug || !accessToken) return;
    api.get(`/public/group-buys/${slug}/p/${accessToken}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Enlace inválido'))
      .finally(() => setLoading(false));
  }, [slug, accessToken]);

  const handlePay = async () => {
    if (!data || !slug) return;
    setPaying(true);
    try {
      const { data: joinRes } = await api.post(`/public/group-buys/${slug}/join`, {
        participant_name: data.participant.name,
        participant_email: 'pending@nominal.local',
        shares_count: 1,
        amount: data.participant.custom_amount,
        payment_method: method,
      });
      window.location.href = joinRes.payment_url;
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se ha podido iniciar el pago');
      setPaying(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center text-ink-500 font-sans">
        Un momento…
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center font-sans">
        <div className="max-w-sm">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Enlace inválido
          </div>
          <h1 className="font-display font-bold text-[28px] text-ink mb-2 tracking-[-0.02em]">
            Este enlace no es válido
          </h1>
          <p className="text-ink-700 text-[14.5px]">
            Pídele al organizador que te lo reenvíe.
          </p>
        </div>
      </div>
    );

  const { participant, group_buy } = data;
  const isPaid = participant.status === 'paid';
  const adelantó = group_buy.organizer_already_paid;

  // Lista de espera
  if (participant.is_waitlist) {
    return (
      <div className="min-h-screen bg-surface text-ink font-sans antialiased flex items-center justify-center px-6">
        <div className="bg-paper border border-line shadow-card rounded-card p-8 max-w-md w-full text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amberCC-700 mb-3">
            · Lista de espera
          </div>
          <h1 className="font-display font-bold text-[28px] text-ink mb-2 tracking-[-0.02em] leading-tight">
            Estás en espera.
          </h1>
          <p className="text-[14.5px] text-ink-700 mb-3">
            Hola <strong className="text-ink">{participant.name}</strong>.
          </p>
          <p className="text-[14px] text-ink-700 leading-relaxed">
            Si alguien se cae de <strong className="text-ink">{group_buy.title}</strong>,
            te avisaremos y podrás pagar tu parte sin tener que pelear por el sitio.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      {/* Header: terracota si es devolución, brand si es pago normal */}
      <header className={adelantó ? 'bg-terra text-paper' : 'bg-brand text-paper'}>
        <div className="max-w-2xl mx-auto px-6 py-12 md:py-14 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-paper/80 mb-3">
            · {adelantó ? 'Devolución para' : 'Pago para'}
          </div>
          <h1 className="font-display font-bold text-[32px] md:text-[40px] leading-[1.04] tracking-[-0.02em] mb-2">
            {group_buy.title}
          </h1>
          <p className="text-[14.5px] text-paper/85">
            {adelantó ? (
              <>
                <strong className="text-paper">{group_buy.organizer_name}</strong> adelantó el dinero.
              </>
            ) : (
              <>
                Organiza: <strong className="text-paper">{group_buy.organizer_name}</strong>
              </>
            )}
          </p>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-10">
        <div className="bg-paper border border-line shadow-card rounded-card p-7 text-center">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-1">
            · Hola
          </div>
          <p className="font-display font-bold text-[24px] text-ink tracking-[-0.01em] leading-tight mb-4">
            {participant.name}
          </p>
          {participant.category && (
            <span className="inline-block px-3 py-1 mb-5 rounded-pill bg-brand-50 text-brand-700 text-[12px] font-semibold">
              {participant.category}
            </span>
          )}

          <div className="border-t border-line-soft pt-5 mb-5">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-2">
              · {adelantó ? `Tienes que devolver a ${group_buy.organizer_name}` : 'Tu parte'}
            </div>
            <p
              className={
                adelantó
                  ? 'font-display font-bold text-[52px] md:text-[64px] leading-none tracking-[-0.03em] text-terra-700'
                  : 'font-display font-bold text-[52px] md:text-[64px] leading-none tracking-[-0.03em] text-olive-700'
              }
            >
              {formatEUR(participant.custom_amount)}
            </p>
          </div>

          {isPaid ? (
            <div className="bg-olive-50 border border-[#C7D5A8] rounded-xl p-4 text-[14px] text-olive-700">
              ✓ Ya pagaste. Gracias.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <SegBtn selected={method === 'stripe'} onClick={() => setMethod('stripe')}>
                  💳 Tarjeta
                </SegBtn>
                <SegBtn selected={method === 'bizum'} onClick={() => setMethod('bizum')}>
                  📱 Bizum
                </SegBtn>
              </div>
              <button
                onClick={handlePay}
                disabled={paying}
                className={
                  adelantó
                    ? 'mt-3 w-full px-4 py-3.5 rounded-xl bg-terra text-paper font-semibold text-[15px] shadow-btn hover:bg-terra-700 transition disabled:bg-[#EFE8DC] disabled:text-[#B2A89A] disabled:shadow-none disabled:cursor-not-allowed'
                    : 'mt-3 w-full px-4 py-3.5 rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition disabled:bg-[#EFE8DC] disabled:text-[#B2A89A] disabled:shadow-none disabled:cursor-not-allowed'
                }
              >
                {paying
                  ? 'Un momento…'
                  : adelantó
                  ? `Devolver mi parte — ${formatEUR(participant.custom_amount)}`
                  : `Pagar ${formatEUR(participant.custom_amount)}`}
              </button>
              <p className="mt-3 text-[12px] text-ink-500">
                Sin coste para ti · Recibirás justificante por email
              </p>
            </>
          )}
        </div>
      </main>

      <footer className="py-8 text-center font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
        Pago seguro · Stripe + Bizum
      </footer>
    </div>
  );
}


function SegBtn({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        selected
          ? 'px-3 py-2.5 rounded-xl text-[13.5px] font-semibold border-[1.5px] border-brand bg-brand-50 text-brand-700 transition'
          : 'px-3 py-2.5 rounded-xl text-[13.5px] font-semibold border-[1.5px] border-line-strong bg-paper text-ink-700 transition hover:border-brand'
      }
    >
      {children}
    </button>
  );
}
